/**
 * Permission codes the Enterprise Dashboard & Inbox declares an interest in.
 *
 * The screen itself is gated on the go-live checklist's read permission
 * (`org.checklist.read`) because configuration completeness is the one widget
 * the screen exists for. Every other widget reads a different endpoint with its
 * own server-side permission, so each one is shown only to a user who holds that
 * endpoint's permission — a widget that would 403 on load is hidden, not shown
 * as an error.
 *
 * Every string below is the exact permission the server route declares
 * (verified against each handler's route table, not inferred from the URL):
 *  - `GET /go-live-checklist`           → `org.checklist.read`
 *  - `GET /foundation/legal-entities`   → `org.legal_entity.read`
 *  - `GET /foundation/divisions`        → `org.division.read`
 *  - `GET /foundation/business-units`   → `foundation.business_unit.read`
 *  - `GET /cost-centers`                → `org.unit.read`
 *  - `GET /positions`                   → `org.position.read`
 *  - `GET /foundation/ledgers`          → `org.ledger.read`
 *  - `GET /approvals`                   → `approval.task.act`
 *  - `GET /attention`                   → `attention.item.read`
 *  - `GET /audit`                       → `audit.log.read`
 */

import { ATTENTION_PERMISSIONS } from '@/modules/attention/permissions'
import { BUSINESS_UNIT_PERMISSIONS } from '@/modules/enterprise/business-units/permissions'
import { DIVISION_PERMISSIONS } from '@/modules/enterprise/divisions/permissions'
import { LEGAL_ENTITY_PERMISSIONS } from '@/modules/enterprise/legal-entities/permissions'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'
import { PERMISSIONS } from '@/lib/permissions'

export const OVERVIEW_PERMISSIONS = {
  read: ORG_PERMISSIONS.checklistRead,

  legalEntityRead: LEGAL_ENTITY_PERMISSIONS.read,
  divisionRead: DIVISION_PERMISSIONS.read,
  businessUnitRead: BUSINESS_UNIT_PERMISSIONS.read,
  costCentreRead: ORG_PERMISSIONS.unitRead,
  positionRead: ORG_PERMISSIONS.positionRead,
  ledgerRead: ORG_PERMISSIONS.ledgerRead,

  approvalAct: PERMISSIONS.approvalAct,
  attentionRead: ATTENTION_PERMISSIONS.itemRead,
  auditRead: PERMISSIONS.auditRead,
} as const
